import { useEffect, useState } from "react";

export type NotificationChannel = "email" | "in_app";

type NotificationOverrideResource = {
  id?: string;
  type: string;
  attributes?: {
    "type-key"?: string;
    channel?: NotificationChannel;
    enabled?: boolean;
  };
};

type NotificationOverrideInput = {
  id?: string;
  type: string;
  attributes: {
    "type-key": string;
    channel: NotificationChannel;
    enabled: boolean;
  };
};

type PreferenceOverride = {
  id: string;
  enabled: boolean;
};

const overrideKey = (typeKey: string, channel: NotificationChannel) =>
  `${typeKey}:${channel}`;

interface UseNotificationChannelOverridesArgs {
  resourceType: string;
  listData: { data?: NotificationOverrideResource[] } | undefined;
  createMutation: (arg: { body: { data: NotificationOverrideInput } }) => {
    unwrap: () => Promise<{ data?: NotificationOverrideResource }>;
  };
  updateMutation: (arg: {
    id: string;
    body: { data: NotificationOverrideInput };
  }) => { unwrap: () => Promise<unknown> };
}

// Shared by the User Profile notification-preferences page and the account-level
// notification-policies admin page: both are lists of {type-key, channel,
// enabled} rows layered on top of the notification-types catalog, and both
// need the same optimistic-toggle / POST-then-PATCH / rollback dance.
export const useNotificationChannelOverrides = ({
  resourceType,
  listData,
  createMutation,
  updateMutation,
}: UseNotificationChannelOverridesArgs) => {
  const [overrides, setOverrides] = useState<
    Record<string, PreferenceOverride>
  >({});
  const [errorMessage, setErrorMessage] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  useEffect(() => {
    if (!listData?.data) return;

    const initialOverrides: Record<string, PreferenceOverride> = {};
    listData.data.forEach((resource) => {
      const typeKey = resource.attributes?.["type-key"];
      const channel = resource.attributes?.channel;
      if (!typeKey || !channel || !resource.id) return;

      initialOverrides[overrideKey(typeKey, channel)] = {
        id: resource.id,
        enabled: !!resource.attributes?.enabled,
      };
    });
    setOverrides(initialOverrides);
  }, [listData]);

  const valueFor = (
    typeKey: string | undefined,
    channel: NotificationChannel,
    fallback: boolean
  ): boolean => {
    const override = typeKey
      ? overrides[overrideKey(typeKey, channel)]
      : undefined;
    return override ? override.enabled : fallback;
  };

  const handleToggle = async (
    typeKey: string,
    channel: NotificationChannel,
    value: boolean
  ) => {
    const key = overrideKey(typeKey, channel);
    const previousOverride = overrides[key];

    setOverrides((prev) => ({
      ...prev,
      [key]: { id: previousOverride?.id ?? "", enabled: value },
    }));

    try {
      if (previousOverride?.id) {
        await updateMutation({
          id: previousOverride.id,
          body: {
            data: {
              id: previousOverride.id,
              type: resourceType,
              attributes: { "type-key": typeKey, channel, enabled: value },
            },
          },
        }).unwrap();
      } else {
        const result = await createMutation({
          body: {
            data: {
              type: resourceType,
              attributes: { "type-key": typeKey, channel, enabled: value },
            },
          },
        }).unwrap();

        const newId = result?.data?.id;
        if (newId) {
          setOverrides((prev) => ({
            ...prev,
            [key]: { id: newId, enabled: value },
          }));
        }
      }
      setShowSuccessAlert(true);
    } catch (error) {
      setOverrides((prev) => {
        const next = { ...prev };
        if (previousOverride) {
          next[key] = previousOverride;
        } else {
          delete next[key];
        }
        return next;
      });
      setErrorMessage(
        "Something went wrong saving this notification preference."
      );
      setShowFailAlert(true);
    }
  };

  return {
    valueFor,
    handleToggle,
    showFailAlert,
    setShowFailAlert,
    showSuccessAlert,
    setShowSuccessAlert,
    errorMessage,
  };
};

export default useNotificationChannelOverrides;
